import { AddressType } from '@bizapp-lp/pjr/molecules/ZipcodeInput';
import {
  DefaultInputState,
  InputProps,
  InputState,
} from '@bizapp-lp/pjr/organisms/InputFormType';
import * as lodash from 'lodash';

export interface GetStepDataResponse {
  [key: string]: string;
}

export const getStepData = async (
  baseUrl: string,
  step: number,
): Promise<GetStepDataResponse> => {
  const method = 'GET';
  const url = `${baseUrl}/api/unauthenticated-users/services/pjr/data/step${
    step + 1
  }`;

  const res = await fetch(url, {
    method: method,
    credentials: 'include',
  });
  if (res.status === 200) {
    return (await res.json()) as GetStepDataResponse;
  } else {
    throw Error('Get Step Data Failed');
  }
};

export const submitStepData = async (
  baseUrl: string,
  step: number,
  bodyObj: { [key: string]: string },
): Promise<void> => {
  const method = 'PUT';
  const url = `${baseUrl}/api/unauthenticated-users/services/pjr/data/step${
    step + 1
  }`;
  const headers = { 'Content-Type': 'application/json' };

  const resp = await fetch(url, {
    method: method,
    headers: headers,
    body: JSON.stringify(bodyObj),
    credentials: 'include',
  });
  if (resp.status !== 204) {
    throw Error('Submit Step Data Failed');
  }
};
export type PatchBody = { [key: string]: string };
export const patchData = async (
  baseUrl: string,
  step: number,
  bodyObj: PatchBody,
): Promise<void> => {
  if (Object.keys(bodyObj).length === 0) {
    // do not send patch when bodyObj is empty
    return;
  }
  const method = 'PATCH';
  const url = `${baseUrl}/api/unauthenticated-users/services/pjr/data/step${
    step + 1
  }`;
  const headers = { 'Content-Type': 'application/json' };

  const resp = await fetch(url, {
    method: method,
    headers: headers,
    body: JSON.stringify(bodyObj),
    credentials: 'include',
  });
  if (resp.status !== 202) {
    throw Error('Patch Step Data Failed');
  }
};

// common functions for step 1 & step 2

export const patchDataWithErrorCallback = async (
  baseUrl: string,
  step: number,
  bodyObj: PatchBody,
  onError: (e: Error) => void = () => {},
) => {
  try {
    await patchData(baseUrl, step, bodyObj);
  } catch (e) {
    // todo: the error is not handled for patch in step pages now
    //  check the session and show proper error
    onError(e);
  }
};

// helper function to return a new copy of form state with partial updates
export const newFormStateOf = (
  id: string,
  stateUpdatePart: Partial<InputState>,
  formState: Map<string, InputState>,
) => {
  const newFormState = lodash.clone(formState);
  newFormState.set(id, {
    ...(newFormState.get(id) ?? DefaultInputState),
    ...stateUpdatePart,
  });
  return newFormState;
};

// helper function to return a new copy of form state with new value
export const newFormStateOfChangedValue = (
  id: string,
  newVal: any,
  formState: Map<string, InputState>,
) => {
  const newFormState = lodash.clone(formState);
  newFormState.set(id, {
    ...(formState.get(id) ?? DefaultInputState),
    value: newVal,
  });
  return newFormState;
};

// helper function to assemble form state to submit data
export const prepareIdValueMap = (
  state: Map<string, InputState>,
  ids: string[],
) => {
  return ids.reduce((obj, key) => {
    const value = state.get(key)?.value;
    obj[key] = value ?? '';
    return obj;
  }, {} as { [key: string]: string });
};

// zipcode fields update
// update relevant fields when change zipcode
export const newFormStateOnAddressChange = (
  addr: AddressType,
  inputProps: InputProps,
  formState: Map<string, InputState>,
) => {
  const logicOptions = inputProps.logicOptions;
  let newFormState = lodash.clone(formState);
  newFormState = newFormStateOfChangedValue(
    logicOptions?.prefectureId as string,
    addr.prefecture,
    newFormState,
  );
  newFormState = newFormStateOfChangedValue(
    logicOptions?.addressId as string,
    addr.address,
    newFormState,
  );
  newFormState = newFormStateOf(
    logicOptions?.prefectureId as string,
    {
      error: false,
    },
    newFormState,
  );
  newFormState = newFormStateOf(
    logicOptions?.addressId as string,
    {
      warning: true,
    },
    newFormState,
  );
  return newFormState;
};
